import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@/components/ui';
import { GradientDots } from '@/components/ui/GradientDots';
import { OperatorWordmark } from '@/components/auth/OperatorWordmark';
import { useAuth } from '../../store/auth-context';
import { setDeviceSignInOptOut } from '../../utils/constrained-device';
import { deviceApi, type DeviceAuthorizeResponse } from '../../services/api';

type FlowState = 'starting' | 'waiting' | 'signing-in' | 'denied' | 'expired' | 'error';

/**
 * Cross-device sign-in — the CONSUMING device's screen (car browser, TV).
 *
 * Starts a device-authorization flow, renders the QR code + user code, and
 * polls the token endpoint. On success the poll response sets the normal auth
 * cookies on THIS browser; we hydrate the auth context via refresh() and
 * enter the app.
 *
 * Poll pacing follows RFC 8628: wait `interval` seconds between polls and
 * back off +5s on `slow_down` (mirrors the optr-identity aws-sso client).
 */
export default function DeviceSignIn() {
  const { refresh, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Deep link that brought the user here (threaded through /signin) — the
  // destination once the device session lands.
  const fromLocation = (location.state as { from?: Location })?.from;
  const destination = fromLocation
    ? `${fromLocation.pathname}${fromLocation.search || ''}`
    : '/';

  const [flow, setFlow] = useState<FlowState>('starting');
  const [device, setDevice] = useState<DeviceAuthorizeResponse | null>(null);
  const [remainingMs, setRemainingMs] = useState(0);
  // Deadline + poll bookkeeping live in refs so the poll loop (a setTimeout
  // chain) never restarts from re-renders.
  const deadlineRef = useRef(0);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const generationRef = useRef(0);

  // Already signed in (e.g. cross-tab broadcast) — straight into the app.
  useEffect(() => {
    if (isAuthenticated) navigate(destination, { replace: true });
  }, [isAuthenticated, destination, navigate]);

  const start = useCallback(async () => {
    // Invalidate any in-flight poll chain from a previous code.
    generationRef.current += 1;
    const generation = generationRef.current;
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);

    setFlow('starting');
    setDevice(null);

    let started: DeviceAuthorizeResponse;
    try {
      started = await deviceApi.authorize();
    } catch {
      if (generation === generationRef.current) setFlow('error');
      return;
    }
    if (generation !== generationRef.current) return;

    deadlineRef.current = Date.now() + started.expires_in * 1000;
    setDevice(started);
    setRemainingMs(started.expires_in * 1000);
    setFlow('waiting');

    let intervalMs = Math.max(started.interval, 1) * 1000;

    const poll = async () => {
      if (generation !== generationRef.current) return;
      if (Date.now() >= deadlineRef.current) {
        setFlow('expired');
        return;
      }

      let result;
      try {
        result = await deviceApi.token(started.device_code);
      } catch {
        // Transient network/server hiccup — keep polling until expiry.
        result = { status: 'pending' as const };
      }
      if (generation !== generationRef.current) return;

      switch (result.status) {
        case 'success':
          setFlow('signing-in');
          await refresh();
          navigate(destination, { replace: true });
          return;
        case 'denied':
          setFlow('denied');
          return;
        case 'expired':
          setFlow('expired');
          return;
        case 'slow_down':
          intervalMs += 5000;
          break;
        case 'pending':
          break;
      }
      pollTimerRef.current = setTimeout(poll, intervalMs);
    };

    pollTimerRef.current = setTimeout(poll, intervalMs);
  }, [navigate, refresh]);

  useEffect(() => {
    start();
    return () => {
      generationRef.current += 1;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [start]);

  // 1s countdown driven off the fixed deadline.
  useEffect(() => {
    if (flow !== 'waiting') return;
    const id = setInterval(() => {
      const left = deadlineRef.current - Date.now();
      setRemainingMs(left);
      if (left <= 0) setFlow('expired');
    }, 1000);
    return () => clearInterval(id);
  }, [flow]);

  return (
    <div
      className="relative min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 py-12"
      style={{ backgroundColor: 'hsl(var(--auth-bg))' }}
    >
      <GradientDots />

      <div className="relative z-10 w-full max-w-3xl rounded-[32px] border border-foreground/15 bg-card/95 dark:bg-card/15 p-6 shadow-[0_80px_160px_-30px_rgba(0,0,0,0.8),0_40px_80px_-20px_rgba(0,0,0,0.6)] backdrop-blur-3xl sm:p-12 md:p-16">
        <div className="flex flex-col items-center text-center">
          <OperatorWordmark size="xl" />
          <p className="mt-6 text-[10px] tracking-[0.15em] sm:text-xs sm:tracking-[0.25em] uppercase text-muted-foreground">
            Sign in with your phone
          </p>
        </div>

        <div className="mt-10 flex min-h-[320px] flex-col items-center justify-center text-center">
          {flow === 'starting' && (
            <p className="text-sm text-muted-foreground">Preparing sign-in…</p>
          )}

          {flow === 'signing-in' && (
            <p className="text-sm text-muted-foreground">Approved — signing you in…</p>
          )}

          {flow === 'waiting' && device && (
            <div className="flex flex-col items-center gap-6">
              <div className="rounded-2xl bg-white p-4">
                <QRCodeSVG
                  value={device.verification_uri_complete}
                  size={208}
                  marginSize={0}
                  aria-label="Sign-in QR code"
                />
              </div>

              <div className="space-y-1.5">
                <p className="text-sm text-muted-foreground">
                  Scan with your phone, or visit{' '}
                  <span className="font-medium text-foreground">{device.verification_uri}</span>{' '}
                  and enter:
                </p>
                <p className="font-mono text-3xl font-semibold tracking-[0.25em] text-foreground">
                  {device.user_code}
                </p>
                <p className="text-xs text-muted-foreground">
                  Make sure the code on your phone matches. Expires in{' '}
                  <span className="text-foreground">{formatCountdown(remainingMs)}</span>.
                </p>
              </div>
            </div>
          )}

          {(flow === 'denied' || flow === 'expired' || flow === 'error') && (
            <div className="flex flex-col items-center gap-5">
              <p className="text-sm text-muted-foreground">
                {flow === 'denied' && 'The request was denied on the other device.'}
                {flow === 'expired' && 'The code expired before the sign-in was approved.'}
                {flow === 'error' && "Couldn't start the sign-in. Please try again."}
              </p>
              <Button onClick={start}>Get a new code</Button>
            </div>
          )}
        </div>

        <p className="mt-10 text-center text-sm text-muted-foreground">
          Prefer to type your password here?{' '}
          {/* Sticky opt-out: constrained devices auto-forward /signin here,
              so choosing email must be remembered (and ?device=0 guards the
              immediate navigation) or the redirect would bounce them back. */}
          <Link
            to="/signin?device=0"
            state={{ from: fromLocation }}
            onClick={() => setDeviceSignInOptOut()}
            className="font-medium text-primary-600 hover:text-primary-500"
          >
            Sign in with email
          </Link>
        </p>
      </div>
    </div>
  );
}

/** `m:ss` countdown. */
function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
